import { Box } from '@mui/material';
import { ReactComponent as SparkIcon } from '../../../assets/icons/Spark.svg';
import { INK, INK_DARK, SPARK_GRADIENT } from './askSyntiroTheme';

export interface AskSyntiroTriggerProps {
  onClick: () => void;
  isOpen: boolean;
}

const COLLAPSE_EASE = 'ease';
const COLLAPSE_MS = 350;
const COLLAPSE_STAGGER_MS = 300;
const FILL_FADE_OVERLAP_MS = 350;
const EXPAND_EASE = 'ease-out';
const EXPAND_MS = 250;
const EXPAND_STAGGER_MS = 0;
const LABEL_WIDTH = 120;

/**
 * Small pill entry point for the navbar, next to the current view's title.
 * Sparkle badge re-derives the retired `forecasting_risk/AskSyntiro` demo's
 * visual language (not imported from it) so the two can diverge freely.
 * Collapses down to just the sparkle badge while the panel is open, sliding right as
 * the label fades out; Expands back into the labelled pill when closed.
 */
export default function AskSyntiroTrigger({ onClick, isOpen }: AskSyntiroTriggerProps) {
  const durationMs = isOpen ? COLLAPSE_MS : EXPAND_MS;
  const staggerMs = isOpen ? COLLAPSE_STAGGER_MS : EXPAND_STAGGER_MS;
  const easing = isOpen ? COLLAPSE_EASE : EXPAND_EASE;
  const fillDelayMs = isOpen ? staggerMs + durationMs - FILL_FADE_OVERLAP_MS : 0;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={isOpen ? 'Close Ask Syntiro' : 'Ask Syntiro'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isOpen ? 0 : 0.75,
        ml: 2,
        px: isOpen ? 0.5 : 0.75,
        py: 0.5,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        borderRadius: '10px',
        border: isOpen ? '1px solid transparent' : '1px solid rgba(255, 255, 255, 0.18)',
        background: isOpen ? 'transparent' : INK,
        color: '#eafaf8',
        fontFamily: (theme) => theme.typography.fontFamily,
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '0.01em',
        cursor: 'pointer',
        contain: 'layout',
        transition: [
          `padding ${durationMs}ms ${easing} ${isOpen ? staggerMs : 0}ms`,
          `gap ${durationMs}ms ${easing} ${isOpen ? staggerMs : 0}ms`,
          `background-color ${durationMs}ms ${easing} ${fillDelayMs}ms`,
          `border-color ${durationMs}ms ${easing} ${fillDelayMs}ms`
        ].join(', '),
        '&:hover': isOpen
          ? undefined
          : {
              borderColor: '#3dd6c8',
              background: INK_DARK
            }
      }}
    >
      <Box
        sx={{
          width: 25,
          height: 25,
          borderRadius: '7px',
          background: SPARK_GRADIENT,
          boxShadow: isOpen ? '0 1px 4px rgba(4, 40, 45, 0.15)' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#04282d',
          flexShrink: 0
        }}
      >
        <SparkIcon
          width={15}
          height={15}
          style={{
            transform: isOpen ? 'rotate(45deg)' : 'none',
            transition: `transform ${durationMs}ms ${easing} ${staggerMs}ms`
          }}
        />
      </Box>
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: isOpen ? 0 : LABEL_WIDTH,
          opacity: isOpen ? 0 : 1,
          transition: [
            `opacity ${durationMs}ms ${easing} ${isOpen ? 0 : staggerMs}ms`,
            `max-width ${durationMs}ms ${easing} ${isOpen ? 0 : staggerMs}ms`
          ].join(', ')
        }}
      >
        Ask Syntiro
      </Box>
    </Box>
  );
}
